import { notify } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { ConfirmModal } from '@affine/component/ui/modal';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService } from '@affine/core/modules/cloud';
import { GlobalContextService } from '@affine/core/modules/global-context';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import {
  WorkspaceService,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../../../../components/hooks/use-navigate-helper';
import { WorkspaceDeleteModal } from './delete';

export const DeleteLeaveWorkspace = ({
  onCloseSetting,
}: {
  onCloseSetting?: () => void;
}) => {
  const {
    workspaceService,
    globalContextService,
    workspacePermissionService,
    workspacesService,
    authService,
  } = useServices({
    WorkspaceService,
    GlobalContextService,
    WorkspacePermissionService,
    WorkspacesService,
    AuthService,
  });
  const t = useI18n();
  const workspace = workspaceService.workspace;
  const { jumpToPage, jumpToIndex } = useNavigateHelper();
  // fixme: cloud regression
  const [showDelete, setShowDelete] = useState(false);
  const [showLeave, setShowLeave] = useState(false);

  const workspaceList = useLiveData(workspacesService.list.workspaces$);
  const currentWorkspaceId = useLiveData(
    globalContextService.globalContext.workspaceId.$
  );

  const isOwner = useLiveData(workspacePermissionService.permission.isOwner$);
  const canDeleteWorkspace = isOwner === true;
  const canLeaveWorkspace = isOwner === false;

  useEffect(() => {
    workspacePermissionService.permission.revalidate();
  }, [workspacePermissionService]);

  useEffect(() => {
    if (!canDeleteWorkspace) {
      setShowDelete(false);
    }
    if (!canLeaveWorkspace) {
      setShowLeave(false);
    }
  }, [canDeleteWorkspace, canLeaveWorkspace]);

  const onLeaveOrDelete = useCallback(() => {
    if (canDeleteWorkspace) {
      setShowDelete(true);
    } else if (canLeaveWorkspace) {
      setShowLeave(true);
    }
  }, [canDeleteWorkspace, canLeaveWorkspace]);

  const navigateAwayFromCurrentWorkspace = useCallback(() => {
    if (currentWorkspaceId === workspace.id) {
      const backWorkspace = workspaceList.find(
        ws => ws.id !== currentWorkspaceId
      );
      // TODO(@eyhn): if there is no workspace, jump to a new page(wait for design)
      if (backWorkspace) {
        jumpToPage(backWorkspace?.id || '', 'all', RouteLogic.REPLACE);
      } else {
        jumpToIndex(RouteLogic.REPLACE);
      }
    }
  }, [
    currentWorkspaceId,
    jumpToIndex,
    jumpToPage,
    workspace.id,
    workspaceList,
  ]);

  const onDeleteConfirm = useAsyncCallback(async () => {
    if (!canDeleteWorkspace) {
      setShowDelete(false);
      return;
    }

    onCloseSetting?.();

    await workspacesService.deleteWorkspace(workspace.meta);
    await authService.signOut().catch(() => undefined);
    window.location.assign('/sign-in');
  }, [
    onCloseSetting,
    workspace.meta,
    canDeleteWorkspace,
    workspacesService,
    authService,
  ]);

  const onLeaveConfirm = useAsyncCallback(async () => {
    if (!canLeaveWorkspace) {
      setShowLeave(false);
      return;
    }

    onCloseSetting?.();
    navigateAwayFromCurrentWorkspace();

    await workspacePermissionService.leaveWorkspace();
    notify.success({ title: t['Successfully deleted']() });
  }, [
    onCloseSetting,
    canLeaveWorkspace,
    navigateAwayFromCurrentWorkspace,
    t,
    workspacePermissionService,
  ]);

  return (
    <>
      <SettingRow
        name={
          <span style={{ color: cssVarV2('status/error') }}>
            {canDeleteWorkspace
              ? t['com.streamlain.workspaceDelete.title']()
              : t['com.streamlain.deleteLeaveWorkspace.leave']()}
          </span>
        }
        desc={t['com.streamlain.deleteLeaveWorkspace.description']()}
        style={{ cursor: isOwner === null ? 'default' : 'pointer' }}
        onClick={onLeaveOrDelete}
        data-testid="delete-workspace-button"
      >
        <ArrowRightSmallIcon />
      </SettingRow>
      {canDeleteWorkspace ? (
        <WorkspaceDeleteModal
          onConfirm={onDeleteConfirm}
          open={showDelete}
          onOpenChange={setShowDelete}
          workspaceMetadata={workspace.meta}
        />
      ) : null}
      {canLeaveWorkspace ? (
        <ConfirmModal
          open={showLeave}
          cancelText={t['com.streamlain.confirmModal.button.cancel']()}
          onConfirm={onLeaveConfirm}
          onOpenChange={setShowLeave}
          title={`${t['com.streamlain.deleteLeaveWorkspace.leave']()}?`}
          description={t[
            'com.streamlain.deleteLeaveWorkspace.leaveDescription'
          ]()}
          confirmText={t['Leave']()}
          confirmButtonOptions={{
            variant: 'error',
          }}
        />
      ) : null}
    </>
  );
};
